import { useState } from 'react'
import { Link } from 'react-router-dom'
import classes from './ChatWidget.module.scss'
import { ChatInvoice } from './types'
import { IconChevron, IconDownload } from './icons'
import { formatChatDate, formatChatMoney } from './chatFormatters'
import { GetEInvoiceAddressApi } from '../../pages/MySpecial/MyOrders/services/getEInvoiceAddressApi'
import { downloadFile, downloadFileFromUrl } from '../../base/utils/dataHelper'

const PAYMENT_PATH = '/myspecial/mypayments'

interface CardProps {
  invoice: ChatInvoice
  disabled?: boolean
}

/** Tek açık fatura kartı: no + şirket + tarih/vade + tutar, "Faturayı İndir" ve "Öde" aksiyonları. */
function InvoiceCard({ invoice, disabled }: CardProps) {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDownload = async () => {
    if (disabled || downloading) return
    setDownloading(true)
    setError(null)
    try {
      const res: string = await new GetEInvoiceAddressApi({} as any).getEInvoiceAddress({
        InvoiceNumber: invoice.eInvoiceNumber,
        DateTime: invoice.invoiceDate,
      })

      if (!res || res === 'Dosya bulunamadı.') {
        setError('Fatura bulunamadı.')
        return
      }

      const fileName = `Fatura-${invoice.invoiceNumber}.pdf`
      if (res.startsWith('http://') || res.startsWith('https://')) {
        downloadFileFromUrl(fileName, res)
      } else {
        const base64 = res.replace(/^data:application\/pdf;base64,/, '')
        downloadFile(fileName, base64, 'application/pdf', true)
      }
    } catch (err: any) {
      setError(err?.description || 'Fatura indirilemedi.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className={classes.invoiceCard}>
      <div className={classes.invoiceCardHead}>
        <div>
          <div className={classes.invoiceNumber}>Fatura No: {invoice.invoiceNumber}</div>
          {invoice.companyName && <div className={classes.invoiceCompany}>{invoice.companyName}</div>}
        </div>
        <div className={classes.invoiceAmount}>{formatChatMoney(invoice.amount, invoice.currency)}</div>
      </div>

      <div className={classes.invoiceCardBody}>
        <div className={classes.invoiceDates}>
          {invoice.invoiceDate && <span>Tarih: {formatChatDate(invoice.invoiceDate)}</span>}
          {invoice.dueDate && <span className={classes.invoiceDueDate}>Vade: {formatChatDate(invoice.dueDate)}</span>}
        </div>

        <div className={classes.invoiceActions}>
          {invoice.eInvoiceNumber && (
            <button
              type='button'
              className={`${classes.orderActionBtn} ${classes.tipTop}`}
              data-tip='Faturayı İndir'
              aria-label='Faturayı İndir'
              onClick={handleDownload}
              disabled={disabled || downloading}
            >
              <IconDownload />
            </button>
          )}
          <Link className={classes.invoicePayBtn} to={PAYMENT_PATH} target='_blank' rel='noopener noreferrer'>
            Öde
          </Link>
        </div>
      </div>

      {error && <div className={classes.orderDetailError}>{error}</div>}
    </div>
  )
}

interface Props {
  invoices: ChatInvoice[]
  disabled?: boolean
}

/** Açık fatura kartlarının listesi. 3'ten fazla fatura varsa alta ödeme sayfası linki eklenir. */
export default function InvoiceList({ invoices, disabled }: Props) {
  if (invoices.length === 0) return null

  return (
    <div className={classes.orderList}>
      {invoices.map((invoice, i) => (
        <InvoiceCard key={`${invoice.invoiceNumber}-${i}`} invoice={invoice} disabled={disabled} />
      ))}

      {invoices.length > 3 && (
        <Link className={classes.viewAllOrdersLink} to={PAYMENT_PATH} target='_blank' rel='noopener noreferrer'>
          Tüm Faturalarım / Ödeme
          <span className={classes.viewAllOrdersChevron}>
            <IconChevron />
          </span>
        </Link>
      )}
    </div>
  )
}
